import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Switch,
  Divider,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { grey, orange } from "@mui/material/colors";
import { FormInputText } from "../../../components/form-components/FormInputText";
import SaveIcon from "@mui/icons-material/Save";
import { subCategorySchema } from "../../../app/schema";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { FormInputDropdown } from "../../../components/form-components/FormInputDropdown";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeSubCategoryList,
} from "../../../redux/counterSlice";
import GeneralImageUpload from "../../../components/form-components/GeneralImageUpload";
import {
  useTablePagination,
  useTableHeight,
} from "../../../hooks/useTablePagination";
import { usePermission } from "../../../hooks/usePermission";

function SubCategory() {
  const dispatch = useDispatch();
  const { taxList, categoryList, confirmModal } = useSelector(
    (state) => state.counter
  );
  const permissions = usePermission("Sub Category");
  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("subCategory", 10);
  const hasActionPermission = permissions?.edit || permissions?.delete;
  const tableHeight = useTableHeight();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(subCategorySchema) });

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [open, setOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // const reloadStore = () => {
  //   axios
  //     .get("/subcategory")
  //     .then(({ data }) => {
  //       dispatch(storeSubCategoryList(data.data || []));
  //     })
  //     .catch((err) => toast.error(err.message));
  // };

  const loadInitialData = () => {
    setLoader(true);
    axios
      .get("/subcategory")
      .then(({ data }) => {
        setFormData(data.data || []);
        // reloadStore();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoader(false));
  };

  useEffect(loadInitialData, [page, rowsPerPage]);

  const toggleStatus = (id) => {
    setLoader(true);
    axios
      .get(`/subCategorySwitch/${id}`)
      .then(loadInitialData)
      .catch((err) => toast.error(err.message))
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal());
      });
  };

  const deleteClickHandler = async (id) => {
    setLoader(true);
    try {
      const { data } = await axios.delete(`/subcategory/${id}`);
      if (!data.error) {
        toast.success(data.message);
        loadInitialData();
      } else {
        dispatch(showModal({ msg: data.message, error: true }));
      }
    } catch (err) {
      dispatch(showModal({ msg: err.message, error: true }));
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal());
    }
  };

  const saveForm = async (values) => {
    setLoader(true);
    try {
      const res = selectedRow?.id
        ? await axios.put(`/subcategory/${selectedRow.id}`, values)
        : await axios.post("/subcategory", values);

      if (!res.data.error) {
        toast.success(res.data.message);
        loadInitialData();
        reset();
        setSelectedRow(null);
      } else {
        dispatch(showModal({ msg: res.data.message, error: true }));
      }
    } catch (err) {
      dispatch(showModal({ msg: err.message, error: true }));
    } finally {
      setLoader(false);
      setOpen(false);
    }
  };

  const openConfirmation = (item, action, label) => {
    setSelectedRow(item);
    dispatch(
      showConfirmModal({ action: () => action(item.id), confirmLabel: label })
    );
  };

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: { filter: false, customBodyRenderLite: (i) => i + 1 },
    },
    {
      name: "sub_category_image",
      label: "Image",
      options: {
        customBodyRenderLite: (i) => {
          const img = formData[i]?.sub_category_image;
          return img ? (
            <PhotoProvider>
              <PhotoView src={img}>
                <img
                  src={img}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <p>-</p>
          );
        },
      },
    },
    { name: "sub_category_name", label: "Sub Category Name" },
    {
      name: "sub_category_name",
      label: "Category Name",
      options: {
        customBodyRenderLite: (i) =>
          formData[i]?.category?.category_name || "-",
      },
    },
    {
      name: "product_count",
      label: "Product Count",
      options: {
        customBodyRenderLite: (i) => (
          <Chip
            label={formData[i]?.product_count}
            color="primary"
            size="small"
          />
        ),
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => {
          const item = formData[i];
          return (
            <Switch
              size="small"
              checked={item.active_status === 1}
              onChange={() =>
                openConfirmation(
                  item,
                  toggleStatus,
                  item.active_status === 1 ? "Deactivate" : "Activate"
                )
              }
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (i) => {
                const item = formData[i];
                const disabled = item.active_status === 1;
                return (
                  <div className="actionBtns">
                    <Button
                      disabled={disabled}
                      onClick={() => {
                        setSelectedRow(item);
                        setOpenImage(true);
                      }}
                    >
                      <AddPhotoAlternateIcon
                        sx={{ color: disabled ? grey[600] : orange[500] }}
                      />
                    </Button>
                    {permissions?.edit && (
                      <Button
                        disabled={disabled}
                        onClick={() => {
                          setSelectedRow(item);
                          setOpen(true);
                        }}
                      >
                        <EditIcon
                          sx={{
                            color: disabled ? grey[600] : "secondary.main",
                          }}
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={disabled}
                        onClick={() =>
                          openConfirmation(item, deleteClickHandler, "Delete")
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: disabled ? grey[600] : "red" }}
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    page,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText: searchText,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4>Sub Category</h4>
          <MUIDataTable
            title={
              permissions.add ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    reset();
                    setSelectedRow(null);
                    setOpen(true);
                  }}
                >
                  New Sub Category
                </Button>
              ) : null
            }
            data={formData}
            columns={columns}
            options={options}
          />
        </>
      )}
      {/* Edit Dialog */}
      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => {
          reset();
          setOpen(false);
        }}
      >
        <DialogTitle>{selectedRow ? "Edit" : "New"} Sub Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInputDropdown
                name="category_id"
                control={control}
                label="Category"
                type="single"
                options={categoryList.map((c) => ({
                  id: c.id,
                  name: c.category_name,
                }))}
                defaultValue={selectedRow?.category_id}
                errors={errors}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputText
                name="sub_category_name"
                control={control}
                label="Sub Category Name"
                defaultValue={selectedRow?.sub_category_name}
                errors={errors}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputText
                name="sub_category_description"
                control={control}
                label="Sub Category Description"
                defaultValue={selectedRow?.sub_category_description}
                errors={errors}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputText
                name="hsn"
                control={control}
                label="HSN Code"
                defaultValue={selectedRow?.hsn}
                errors={errors}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputDropdown
                name="tax_id"
                control={control}
                label="Tax"
                type="single"
                options={taxList.map((t) => ({
                  id: t.tax_id,
                  name: `${t.tax_percentage}%`,
                }))}
                defaultValue={selectedRow?.tax_id}
                errors={errors}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit(saveForm)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Image Upload Dialog */}
      <Dialog
        open={openImage}
        maxWidth="xs"
        onClose={() => setOpenImage(false)}
      >
        <DialogTitle className="text-primary">Update Image</DialogTitle>
        <Divider variant="middle" className="bg-primary" />

        <DialogContent>
          <Grid container columnSpacing={{ xs: 4 }}>
            <Grid item xs={12}>
              <GeneralImageUpload
                status={selectedRow?.sub_category_image}
                name={selectedRow?.id?.toString()}
                control={control}
                label={selectedRow?.category_name}
                setValue={setValue}
                type="text"
                defaultValue={selectedRow?.sub_category_image}
                loadInitialData={loadInitialData}
                url="imageUpdateSubCategory"
                formDataKey={{ id: "id", image: "sub_category_image" }}
                dimension="220 x 160"
                setOpenImage={setOpenImage}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenImage(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmModal?.flag}
        onClose={() => dispatch(hideConfirmModal({ msg: "" }))}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {selectedRow?.confirmLabel}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => dispatch(hideConfirmModal())}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              confirmModal.action();
              dispatch(hideConfirmModal());
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SubCategory;
